/* eslint-disable @typescript-eslint/no-explicit-any */
import {displayFieldsToObjects} from '@/lib/classes/InfoWindowContent/DisplayField';
import {omitProperties, onlyOwnProperties} from '@/lib/ReusableFunctions/Object';
import {MapService} from '@/services/MapService';
import {geohashForLocation} from 'geofire-common';
import type {FeatureManagerUpdateData} from '@/lib/classes/FeatureManager/types';

type FeatureLike = {
  toJSON?: () => Record<string, any>;
  graphicData?: Record<string, any>;
  comps?: object[];
  displayFields?: object[];
};

/**
 * Given a feature's data, returns its object to be saved to Firestore.
 * @param featureData Feature, FeatureData, or Graphic
 * @returns FeatureManagerUpdateData
 */
export const getFirestoreUpdateFromFeature = (
  featureData: FeatureLike,
): FeatureManagerUpdateData => {
  let update: Record<string, any> | undefined = featureData.toJSON?.();

  // If the feature cannot convert itself to JSON, manually create the update object.
  if (!featureData.toJSON) {
    const omitProps = [
      'parent',
      'id',
      'graphic',
      'center',
      'icon',
      'isEditing',
      'infoWindowContent',
      'visible',
      // A read cache of graphicData.linkedLabel, which is what gets saved
      'linkedLabel',
    ] as const;
    update = omitProperties(onlyOwnProperties(featureData), ...omitProps);

    if ('graphicData' in featureData) {
      update['graphicData'] = featureData['graphicData'];
    }
    if (update.comps) {
      update.comps = update.comps.map((comp: any) => {
        // Remove the parent property from each comp before saving to Firestore
        const {parent, ...compWithoutParent} = comp;
        return {
          ...compWithoutParent,
          displayFields: displayFieldsToObjects(compWithoutParent.displayFields),
        };
      });
    }
    if (update.displayFields) {
      update.displayFields = displayFieldsToObjects(update.displayFields);
    }
  }

  // If the `bounds` are updated, the `center` and `geohash` must be recomputed.
  if (update && 'bounds' in update) {
    const center = MapService.calculateCenter(update.bounds);
    update.geohash = geohashForLocation([center.lat(), center.lng()]);
  }

  return update as FeatureManagerUpdateData;
};
